<template>
  <div class="sci-page-wrapper" style="font-family: 'Inter', sans-serif;">
    <div class="max-w-[1280px] mx-auto px-4 space-y-8">
      
      <!-- Welcome Header -->
      <div class="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <h1 class="sci-page-title-only">Painel de Controle (Peças e Serviços)</h1>
          <p class="text-slate-500 font-medium mt-1">Visão geral do controle de OS, peças, serviços e saldo de licitações</p>
        </div>
        <div class="flex items-center gap-2">
          <v-btn icon color="slate-600" @click="initialize" :loading="loader">
            <v-icon small>mdi-refresh</v-icon>
          </v-btn>
        </div>
      </div>

      <!-- Prime KPIs (Finance and Volume) -->
      <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
        <!-- Total OSs -->
        <div class="bg-gradient-to-br from-slate-800 to-slate-900 rounded-3xl p-8 shadow-2xl shadow-slate-900/10 relative overflow-hidden transition-transform duration-300 hover:-translate-y-1 hover:shadow-slate-900/20">
          <div class="absolute -right-10 -top-10 opacity-5">
            <v-icon size="180" color="white">mdi-file-document-outline</v-icon>
          </div>
          <div class="relative z-10 flex flex-col h-full justify-between min-h-[140px]">
            <div class="flex items-center gap-3 mb-6">
              <div class="bg-white/10 p-2.5 rounded-xl border border-white/5">
                <v-icon color="white" small>mdi-clipboard-list-outline</v-icon>
              </div>
              <h3 class="text-white/80 font-bold uppercase tracking-widest text-xs">Total de Ordens (OS)</h3>
            </div>
            <div>
              <p class="text-4xl lg:text-5xl font-black text-white tracking-tighter">
                <span class="font-mono">{{ data.osTotalCount || 0 }}</span>
              </p>
              <p class="text-slate-400 font-bold text-xs mt-3 flex items-center gap-1">
                Total acumulado na plataforma
              </p>
            </div>
          </div>
        </div>

        <!-- Saída de Peças -->
        <div class="bg-gradient-to-br from-orange-500 to-[#FF4600] rounded-3xl p-8 shadow-2xl shadow-orange-500/20 relative overflow-hidden transition-transform duration-300 hover:-translate-y-1 hover:shadow-orange-500/30">
          <div class="absolute -right-6 -bottom-6 opacity-[0.08] transform rotate-12">
            <v-icon size="160" color="white">mdi-cog-outline</v-icon>
          </div>
          <div class="relative z-10 flex flex-col h-full justify-between min-h-[140px]">
            <div class="flex items-center gap-3 mb-6">
              <div class="bg-white/20 p-2.5 rounded-xl border border-white/10">
                <v-icon color="white" small>mdi-wrench-outline</v-icon>
              </div>
              <h3 class="text-white/90 font-bold uppercase tracking-widest text-xs">Saída de Peças (R$)</h3>
            </div>
            <div>
              <p class="text-3xl lg:text-4xl font-black text-white tracking-tighter">
                R$ <span class="font-mono">{{ formatCurrency(data.totalParts) }}</span>
              </p>
              <p class="text-white/90 font-bold text-xs mt-3 bg-white/20 w-fit px-3 py-1 rounded-full border border-white/10">
                Total liberado de peças
              </p>
            </div>
          </div>
        </div>

        <!-- Serviços Realizados -->
        <div class="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-3xl p-8 shadow-2xl shadow-blue-500/20 relative overflow-hidden transition-transform duration-300 hover:-translate-y-1 hover:shadow-blue-500/30">
          <div class="absolute -right-6 -bottom-6 opacity-[0.08] transform rotate-12">
            <v-icon size="160" color="white">mdi-account-wrench-outline</v-icon>
          </div>
          <div class="relative z-10 flex flex-col h-full justify-between min-h-[140px]">
            <div class="flex items-center gap-3 mb-6">
              <div class="bg-white/20 p-2.5 rounded-xl border border-white/10">
                <v-icon color="white" small>mdi-hammer-wrench</v-icon>
              </div>
              <h3 class="text-white/90 font-bold uppercase tracking-widest text-xs">Serviços Feitos (R$)</h3>
            </div>
            <div>
              <p class="text-3xl lg:text-4xl font-black text-white tracking-tighter">
                R$ <span class="font-mono">{{ formatCurrency(data.totalServices) }}</span>
              </p>
              <p class="text-white/90 font-bold text-xs mt-3 bg-white/20 w-fit px-3 py-1 rounded-full border border-white/10">
                Total de mão de obra executada
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Secondary KPIs -->
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <!-- Card 1 -->
        <div class="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm flex items-center gap-5 hover:shadow-md transition-shadow">
          <div class="bg-blue-50 w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0 border border-blue-100">
            <v-icon color="blue" size="28">mdi-storefront-outline</v-icon>
          </div>
          <div>
            <p class="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Fornecedores</p>
            <p class="text-3xl font-black text-slate-700 leading-none">{{ data.providersCount || 0 }}</p>
          </div>
        </div>
        
        <!-- Card 2 -->
        <div class="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm flex items-center gap-5 hover:shadow-md transition-shadow">
          <div class="bg-emerald-50 w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0 border border-emerald-100">
            <v-icon color="success" size="28">mdi-file-cabinet</v-icon>
          </div>
          <div>
            <p class="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Licitações</p>
            <p class="text-3xl font-black text-slate-700 leading-none">{{ data.biddingsCount || 0 }}</p>
          </div>
        </div>

        <!-- Card 3 -->
        <div class="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm flex items-center gap-5 hover:shadow-md transition-shadow">
          <div class="bg-amber-50 w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0 border border-amber-100">
            <v-icon color="amber" size="28">mdi-clock-alert-outline</v-icon>
          </div>
          <div>
            <p class="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">OS Pendentes</p>
            <p class="text-3xl font-black text-slate-700 leading-none">{{ data.osPendingCount || 0 }}</p>
          </div>
        </div>

        <!-- Card 4 -->
        <div class="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm flex items-center gap-5 hover:shadow-md transition-shadow">
          <div class="bg-purple-50 w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0 border border-purple-100">
            <v-icon color="purple" size="28">mdi-check-decagram-outline</v-icon>
          </div>
          <div>
            <p class="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">OS Liberadas</p>
            <p class="text-3xl font-black text-slate-700 leading-none">{{ data.osCompletedCount || 0 }}</p>
          </div>
        </div>
      </div>

      <!-- Charts Data -->
      <div class="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <!-- Consumo Graph -->
        <div class="bg-white rounded-3xl border border-slate-200 shadow-sm p-8 lg:col-span-8">
          <div class="flex items-center justify-between mb-8">
            <h3 class="text-sm font-black text-slate-800 uppercase tracking-widest">Maiores Gastos em OS por Veículo (R$)</h3>
            <span class="bg-slate-100 text-slate-500 text-[10px] px-3 py-1.5 rounded-lg font-bold uppercase tracking-widest">TOP 5</span>
          </div>
          <div style="height: 350px;" class="w-full">
            <TechBarChart 
              v-if="chartLabels.length > 0"
              :chartData="chartValues" 
              :labels="chartLabels" 
            />
            <div v-else class="h-full flex flex-col items-center justify-center text-slate-400">
               <v-icon size="64" color="slate-200" class="mb-4">mdi-chart-bar-stacked</v-icon>
               <span class="text-[11px] font-bold uppercase tracking-widest text-slate-400">Nenhum gasto registrado em ordens concluídas</span>
            </div>
          </div>
        </div>

        <!-- Status Breakdown Card -->
        <div class="bg-white rounded-3xl border border-slate-200 shadow-sm p-8 lg:col-span-4 flex flex-col justify-between">
          <div>
            <h3 class="text-sm font-black text-slate-800 uppercase tracking-widest mb-6">Status das Ordens</h3>
            
            <div class="space-y-6">
              <!-- Item 1: Concluidas -->
              <div class="flex justify-between items-center">
                <div class="flex items-center gap-3">
                  <div class="w-3.5 h-3.5 rounded-full bg-emerald-500"></div>
                  <span class="text-xs font-bold text-slate-600 uppercase">Liberadas (Concluídas)</span>
                </div>
                <span class="text-sm font-black text-slate-800 font-mono">{{ data.osCompletedCount || 0 }}</span>
              </div>

              <!-- Item 2: Pendentes -->
              <div class="flex justify-between items-center">
                <div class="flex items-center gap-3">
                  <div class="w-3.5 h-3.5 rounded-full bg-amber-500"></div>
                  <span class="text-xs font-bold text-slate-600 uppercase">Aguardando Liberação</span>
                </div>
                <span class="text-sm font-black text-slate-800 font-mono">{{ data.osPendingCount || 0 }}</span>
              </div>

              <!-- Item 3: Canceladas -->
              <div class="flex justify-between items-center">
                <div class="flex items-center gap-3">
                  <div class="w-3.5 h-3.5 rounded-full bg-rose-500"></div>
                  <span class="text-xs font-bold text-slate-600 uppercase">Canceladas / Recusadas</span>
                </div>
                <span class="text-sm font-black text-slate-800 font-mono">{{ data.osCancelledCount || 0 }}</span>
              </div>
            </div>
          </div>

          <div class="pt-8 border-t border-slate-100 mt-8">
            <div class="bg-orange-50/50 border border-orange-100 rounded-2xl p-4 flex items-center justify-between">
              <span class="text-[10px] font-black text-orange-500 uppercase tracking-widest">Total Geral Liberado</span>
              <span class="text-lg font-black text-slate-700 font-mono">R$ {{ formatCurrency(data.totalGlobal) }}</span>
            </div>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import TechBarChart from "./components/TechBarChart";

export default {
  components: {
    TechBarChart
  },
  props: ["prefecture_id"],
  data: () => ({
    loader: true,
    data: {
      providersCount: 0,
      biddingsCount: 0,
      osTotalCount: 0,
      osPendingCount: 0,
      osCompletedCount: 0,
      osCancelledCount: 0,
      totalParts: "0.00",
      totalServices: "0.00",
      totalGlobal: "0.00",
      topVehicles: []
    }
  }),
  computed: {
    chartLabels() {
      return this.data.topVehicles ? this.data.topVehicles.map(v => v.board) : [];
    },
    chartValues() {
      return this.data.topVehicles ? this.data.topVehicles.map(v => v.total) : [];
    }
  },
  created() {
    this.initialize();
  },
  methods: {
    initialize() {
      this.loader = true;
      const params = {};
      if (this.prefecture_id) params.prefecture_id = this.prefecture_id;
      if (this.$store.getters.getUserData.table_key === 'providers') {
        params.provider_id = this.$store.getters.getUserData.table_id;
      }

      this.$http
        .get("/parts-and-services/os/dashboard-data", { params })
        .then((r) => {
          this.data = r.data;
          this.loader = false;
        })
        .catch(() => {
          this.loader = false;
        });
    },
    formatCurrency(val) {
      const num = parseFloat(val) || 0;
      return num.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
  }
};
</script>

<style scoped>
</style>
